import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Paper, Popper, Tooltip, Typography } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { useThemeColor } from '../../hooks/useThemeColor';
import { sharedColors } from '../../utils/Style';
import { AddToBasketPopper } from '../../components/ProductCard/AddToBasketPopper';
import { formatCurrency } from '../../utils/Currency';

type EligibleFreeProductCardProps = {
  product: ProductSummary;
};

const EligibleFreeProductCard = ({ product }: EligibleFreeProductCardProps) => {
  const t = useLocalizedText();

  const { primary, secondary, ternary } = useThemeColor();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Link to={`/urun/${product.slug}`} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          p: 1.5,
          boxSizing: 'border-box',
          m: 0.5,
          border: '1px solid',
          borderColor: sharedColors.gray4,
          borderRadius: '6px',
        }}
      >
        <Box
          component='img'
          src={product.imagePath}
          width='auto'
          height={100}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Tooltip title={product.name}>
            <Typography
              sx={{
                maxWidth: 200,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                fontWeight: 600,
                color: sharedColors.gray5,
              }}
            >
              {product.name}
            </Typography>
          </Tooltip>

          <Typography sx={{ color: primary, fontSize: '12px' }}>
            {t('basket.eligible_free_products.free')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: sharedColors.gray4,
                textDecoration: 'line-through',
              }}
            >
              {product.priceFormatted}
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: primary,
              }}
            >
              {formatCurrency(0)}
            </Typography>
          </Box>

          <Button
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();

              setAnchorEl(ev.currentTarget);
            }}
            sx={{
              fontWeight: 600,
              fontSize: '13px',
              padding: '8px 15px',
              width: 'fit-content',
              color: secondary,
              backgroundColor: primary,
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: ternary,
              },
              textTransform: 'none',
              mt: 0.5,
            }}
          >
            {t('product.add_to_cart')}
          </Button>

          <Popper open={!!anchorEl} anchorEl={anchorEl}>
            <AddToBasketPopper
              productSlug={product.slug}
              onClose={() => setAnchorEl(null)}
              atCheckout={false}
              forcedQuantity={1}
            />
          </Popper>
        </Box>
      </Box>
    </Link>
  );
};

type EligibleFreeProductsCardProps = {
  products: ProductSummary[];
};

export const EligibleFreeProductsCard = ({
  products,
}: EligibleFreeProductsCardProps) => {
  const t = useLocalizedText();

  const { primary } = useThemeColor();

  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1.5,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.5,
        }}
      >
        <CelebrationIcon sx={{ color: primary }} />

        <Typography sx={{ fontWeight: 600 }}>
          {t('basket.eligible_free_products.congratulations')}
        </Typography>
      </Box>

      <Typography sx={{ color: sharedColors.gray5, fontSize: '14px' }}>
        {t('basket.eligible_free_products.description')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          overflowX: 'auto',
          gap: 1.5,
        }}
      >
        {products.map((product) => (
          <EligibleFreeProductCard key={product.id} product={product} />
        ))}
      </Box>
    </Paper>
  );
};
